import { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { StringParam, useQueryParams, withDefault } from 'use-query-params'
import { ActivityLogDateRange } from 'src/domain/activityLog'
import {
  formatSimpleDate,
  toJstEndOfDayISO,
  toJstStartOfDayISO,
} from 'src/utils/date'

type UseActivityLogDateRangeOptions = {
  // 指定時は「baseDate 〜 min(baseDate + 1ヶ月, 今日)」をデフォルトにする
  // (確定履歴で帳票日を起点にするケース)。未指定なら「今日 - 1ヶ月 〜 今日」
  // NOTE: デフォルトはクエリ未設定の初回のみ反映される。baseDate が同一マウント中に
  // 変わってもクエリ値は追従しない (現状 baseDate を変える導線は画面遷移 =
  // リマウントを伴うため問題ない)
  baseDate?: string
}

type UseActivityLogDateRangeResult = {
  startDate: string
  endDate: string
  // 範囲の幅を 1 年以内に保つための DatePicker 制約 (開始の下限 / 終了の上限)
  startMinDate?: string
  endMaxDate: string
  fromISO: string
  toISO: string
  onChangeDateRange: (range: ActivityLogDateRange) => void
}

export const useActivityLogDateRange = ({
  baseDate,
}: UseActivityLogDateRangeOptions = {}): UseActivityLogDateRangeResult => {
  // 0 時跨ぎや無駄な再 fetch を避けるため mount 時に固定する。
  // maxDate も today 由来のため、長時間開いたまま日付を跨ぐと DatePicker で
  // 「今日」が選べなくなる点に注意 (長時間放置時のみのレアケース)
  const today = useMemo(() => dayjs(), [])
  const defaultStartDate = useMemo(
    () =>
      formatSimpleDate(baseDate ? dayjs(baseDate) : today.subtract(1, 'month')),
    [baseDate, today]
  )
  const defaultEndDate = useMemo(() => {
    if (!baseDate) return formatSimpleDate(today)
    // デフォルトは帳票日から 1 ヶ月分。今日を超えないよう早い方に丸める
    const oneMonthFromBase = dayjs(baseDate).add(1, 'month')
    return formatSimpleDate(
      oneMonthFromBase.isBefore(today) ? oneMonthFromBase : today
    )
  }, [baseDate, today])
  const maxDate = useMemo(() => formatSimpleDate(today), [today])

  const [{ activityLogStartDate, activityLogEndDate }, setQuery] =
    useQueryParams({
      activityLogStartDate: withDefault(StringParam, defaultStartDate),
      activityLogEndDate: withDefault(StringParam, defaultEndDate),
    })

  const onChangeDateRange = useCallback(
    (range: ActivityLogDateRange) => {
      setQuery({
        activityLogStartDate: range.start,
        activityLogEndDate: range.end,
      })
    },
    [setQuery]
  )

  // 売上報告ログ (baseDate = 売上日) では売上日より前にログが存在しないため、
  // 売上日を開始日の下限とする。URL クエリ改ざんやブックマークで売上日より前が
  // 渡った場合に備え、表示・fetch に使う実効値も売上日に丸める。
  const minStartDate = useMemo(
    () =>
      baseDate && dayjs(baseDate).isValid()
        ? formatSimpleDate(dayjs(baseDate))
        : undefined,
    [baseDate]
  )

  const [normalizedStartDate, normalizedEndDate] = useMemo(() => {
    const [start, end] =
      activityLogStartDate <= activityLogEndDate
        ? [activityLogStartDate, activityLogEndDate]
        : [activityLogEndDate, activityLogStartDate]
    if (!minStartDate) return [start, end]
    // 売上日より前は範囲に含めない (終了も売上日を下回る場合は売上日に揃える)
    return [
      start < minStartDate ? minStartDate : start,
      end < minStartDate ? minStartDate : end,
    ]
  }, [activityLogStartDate, activityLogEndDate, minStartDate])
  // 範囲の幅を 1 年以内に保つ。開始は「終了 - 1年」以降、終了は「開始 + 1年」と今日の早い方まで。
  // baseDate (売上日) 指定時は売上日を開始の下限にする (1年制約は endMaxDate 側で担保)。
  const startMinDate = useMemo(() => {
    if (minStartDate) return minStartDate
    const end = dayjs(normalizedEndDate)
    return end.isValid() ? formatSimpleDate(end.subtract(1, 'year')) : undefined
  }, [minStartDate, normalizedEndDate])
  const endMaxDate = useMemo(() => {
    const start = dayjs(normalizedStartDate)
    if (!start.isValid()) return maxDate
    const oneYearFromStart = start.add(1, 'year')
    return oneYearFromStart.isBefore(today)
      ? formatSimpleDate(oneYearFromStart)
      : maxDate
  }, [normalizedStartDate, today, maxDate])

  const fromISO = useMemo(
    () => toJstStartOfDayISO(normalizedStartDate),
    [normalizedStartDate]
  )
  const toISO = useMemo(
    () => toJstEndOfDayISO(normalizedEndDate),
    [normalizedEndDate]
  )

  return {
    startDate: normalizedStartDate,
    endDate: normalizedEndDate,
    startMinDate,
    endMaxDate,
    fromISO,
    toISO,
    onChangeDateRange,
  }
}
